import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../config/axiosConfig';
import { MdArrowBack, MdDateRange, MdTranslate } from 'react-icons/md';
import { SUPPORTED_LANGUAGES, translateNewsItem } from '../utils/translator';
import SEO from '../components/SEO';

const NaiNewsDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [news, setNews] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Multilingual translation states
    const [selectedLang, setSelectedLang] = useState('original');
    const [translatingLang, setTranslatingLang] = useState(null);
    const [translations, setTranslations] = useState({});
    const [translationError, setTranslationError] = useState(null);

    useEffect(() => {
        const fetchNewsDetail = async () => {
            try {
                const response = await axios.get(`nai-news/${id}/`);
                setNews(response.data);
                if (response.data.translations && typeof response.data.translations === 'object') {
                    setTranslations(response.data.translations);
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching news detail:", err);
                setError("Failed to load news details.");
                setLoading(false);
            }
        };

        fetchNewsDetail();
    }, [id]);

    const handleLanguageSelect = async (langCode) => {
        if (langCode === selectedLang) return;
        setTranslationError(null);

        if (langCode === 'original') {
            setSelectedLang('original');
            return;
        }

        // Check if translation already exists (from backend or cached)
        if (translations[langCode] && (translations[langCode].title || translations[langCode].description)) {
            setSelectedLang(langCode);
            return;
        }

        try {
            setTranslatingLang(langCode);
            const result = await translateNewsItem(news, langCode);
            if (result) {
                setTranslations(prev => ({
                    ...prev,
                    [langCode]: result
                }));
                setSelectedLang(langCode);
            }
        } catch (err) {
            console.error("Translation error:", err);
            setTranslationError("Could not translate news to the selected language. Please try again.");
        } finally {
            setTranslatingLang(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error || !news) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center px-4">
                 <h2 className="text-2xl font-bold text-gray-800 mb-4">News Not Found</h2>
                 <p className="text-gray-600 mb-6">{error || "The news article you are looking for does not exist."}</p>
                 <button 
                    onClick={() => navigate('/nai-news')}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                 >
                    <MdArrowBack className="mr-2" /> Back to News
                 </button>
            </div>
        );
    }

    const displayTitle = (translations[selectedLang] && translations[selectedLang].title) || news.title;
    const displayDescription = (translations[selectedLang] && translations[selectedLang].description) || news.description;
    const currentLangObj = SUPPORTED_LANGUAGES.find(l => l.code === selectedLang);

    const fullImageUrl = news.image
        ? (news.image.startsWith('http') ? news.image : `https://api.naiindia.in${news.image}`)
        : "https://naiindia.in/images/nailogo.png";

    return (
        <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <SEO
                title={`${displayTitle} - Newsmedia Association of India`}
                description={displayDescription ? displayDescription.substring(0, 160).replace(/\n/g, ' ') : "Newsmedia Association of India"}
                ogTitle={displayTitle}
                ogDescription={displayDescription ? displayDescription.substring(0, 160).replace(/\n/g, ' ') : "Newsmedia Association of India"}
                ogImage={fullImageUrl}
                ogUrl={`https://naiindia.in/nai-news/${id}`}
            />
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Image Section - Display full image without cropping on large devices */}
                {news.image && (
                    <div className="w-full relative bg-slate-900/5 flex items-center justify-center overflow-hidden">
                        <img 
                            src={news.image} 
                            alt={news.title} 
                            className="w-full h-auto max-h-[600px] object-contain sm:object-contain mx-auto"
                        />
                         <div className="absolute top-4 left-4">
                            <button 
                                onClick={() => navigate('/nai-news')}
                                className="bg-white/90 hover:bg-white text-gray-800 px-4 py-2 rounded-full shadow-md flex items-center transition-colors duration-200"
                            >
                                <MdArrowBack className="mr-2" /> Back
                            </button>
                        </div>
                    </div>
                )}
                 
                 {!news.image && (
                     <div className="p-6">
                        <button 
                                onClick={() => navigate('/nai-news')}
                                className="flex items-center text-gray-600 hover:text-gray-900"
                            >
                                <MdArrowBack className="mr-2" /> Back to News
                        </button>
                     </div>
                 )}

                {/* Bar Below Image - Center on Mobile, Right-aligned on Desktop */}
                <div className="bg-slate-50/90 border-b border-gray-100 px-4 sm:px-8 py-3.5 flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center justify-center sm:justify-start text-sm text-gray-500 w-full sm:w-auto">
                        <MdDateRange className="mr-2 h-4 w-4 text-blue-600" />
                        <span className="font-medium text-gray-600">
                            {new Date(news.created_at).toLocaleDateString(undefined, {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                            })}
                        </span>
                        {selectedLang !== 'original' && currentLangObj && (
                            <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                                <MdTranslate className="mr-1 h-3.5 w-3.5" />
                                Reading in {currentLangObj.name} ({currentLangObj.nativeName})
                            </span>
                        )}
                    </div>

                    {/* 5 Translation Buttons - Centered on mobile, right-aligned on desktop */}
                    <div className="flex flex-wrap items-center justify-center sm:justify-end gap-2 w-full sm:w-auto sm:ml-auto">
                        {selectedLang !== 'original' && (
                            <button
                                onClick={() => handleLanguageSelect('original')}
                                className="text-xs font-semibold px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 transition-colors shadow-sm flex items-center"
                                title="Revert to original language"
                            >
                                Original
                            </button>
                        )}
                        {SUPPORTED_LANGUAGES.map((lang) => {
                            const isActive = selectedLang === lang.code;
                            const isLoading = translatingLang === lang.code;

                            return (
                                <button
                                    key={lang.code}
                                    onClick={() => handleLanguageSelect(lang.code)}
                                    disabled={isLoading}
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all duration-150 flex items-center shadow-sm ${
                                        isActive
                                            ? 'bg-blue-600 border-blue-600 text-white shadow'
                                            : 'border-gray-300 text-gray-700 bg-white hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700'
                                    } ${isLoading ? 'opacity-70 cursor-wait' : ''}`}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="animate-spin inline-block w-3 h-3 border-2 border-current border-t-transparent rounded-full mr-1.5"></span>
                                            Translating...
                                        </>
                                    ) : (
                                        <>
                                            {isActive && <span className="mr-1 text-white font-bold">✓</span>}
                                            {lang.label}
                                        </>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Content Section */}
                <div className="p-6 sm:p-8">
                    {translationError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-center">
                            <span>{translationError}</span>
                        </div>
                    )}

                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 mb-6 leading-tight">
                        {displayTitle}
                    </h1>

                    <div className="text-[16px] text-gray-800 max-w-none whitespace-pre-line mb-8 leading-relaxed font-normal" style={{ fontSize: '16px', lineHeight: '1.8' }}>
                        {displayDescription}
                    </div>

                    {/* Additional Images Grid */}
                    {news.images && news.images.length > 0 && (
                        <div className="mt-8 border-t pt-8">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Gallery</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {news.images.map((img) => (
                                    <div key={img.id} className="rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                        <img 
                                            src={img.image} 
                                            alt={`Gallery for ${news.title}`} 
                                            className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                                            onClick={() => window.open(img.image, '_blank')}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NaiNewsDetail;
